/**
 * What she says when you arrive.
 *
 * The greeting is the one thing she says without being asked, so it is the one
 * place where getting the small details wrong is most obvious. Two of them:
 *
 *  - The part of day is read from the operator's own clock, not the server's.
 *    A night shift in Madinah signing in at 02:00 is having an evening, and a
 *    greeting that insists otherwise because a server in another region says so
 *    is the kind of small wrongness that makes people stop reading.
 *
 *  - The name is the first name only. `principal.name` arrives as "Mona
 *    Al-Harbi" or, on development auth, as "sup.mona" — an id, not a name. An
 *    id is not something to greet someone by, so it is trimmed to something
 *    speakable and, when it clearly is not a name at all, left out entirely.
 *    "Good morning." is a perfectly good greeting; "Good morning, sup.mona." is
 *    not.
 */

export type PartOfDay = "morning" | "afternoon" | "evening";

/**
 * Morning until noon, afternoon until 17:00, evening after — including the
 * small hours, which belong to the shift that is still on rather than to the
 * calendar day that has technically started.
 */
export function partOfDay(at: Date): PartOfDay {
  const h = at.getHours();
  if (h >= 5 && h < 12) return "morning";
  if (h >= 12 && h < 17) return "afternoon";
  return "evening";
}

/**
 * The speakable part of a principal's name, or null when there isn't one.
 *
 * Development auth hands us external ids like "sup.mona"; API keys hand us
 * things like "svc-platform-feed". Neither is a name, and greeting someone by
 * their service account is the kind of detail that tells an operator precisely
 * how much attention went into the rest of the screen.
 *
 * Two rules, both chosen for what they let through rather than what they catch.
 * Letters, apostrophes and hyphens only — so "Al-Harbi", "O'Neill" and "منى"
 * survive, while a dot, an underscore or a digit is taken as proof of an id. And
 * at most one hyphen: real names hyphenate once, slug-shaped ids keep going.
 */
export function firstName(full: string | undefined | null): string | null {
  if (!full) return null;
  const first = full.trim().split(/\s+/)[0];
  if (!first || first.length < 2) return null;
  if (!/^[\p{L}][\p{L}'-]*$/u.test(first)) return null;
  if ((first.match(/-/g) ?? []).length > 1) return null;
  return first;
}

/** "Good morning, Mona." — or "Good morning." when there is no name to use. */
export function greeting(name: string | undefined | null, at: Date): string {
  const part = partOfDay(at);
  const who = firstName(name);
  return who ? `Good ${part}, ${who}.` : `Good ${part}.`;
}

/**
 * The same greeting for someone who has been here before.
 *
 * "Welcome back" only earns its place when it is true, so the caller passes the
 * last visit and gets the plain greeting when there isn't one. Greeting a
 * first-time user as though they were returning is a small lie that costs the
 * rest of the screen some credibility.
 */
export function welcomeBack(name: string | undefined | null, at: Date, returning: boolean): string {
  if (!returning) return greeting(name, at);
  const who = firstName(name);
  return who ? `Welcome back, ${who}.` : "Welcome back.";
}

/**
 * The sign-off. Evening gets "Good night" rather than "Good evening" — the
 * difference between hello and goodbye is most of what a farewell is for, and
 * an assistant that says "Good evening" on the way out sounds like it did not
 * notice you were leaving.
 */
export function farewell(name: string | undefined | null, at: Date): string {
  const who = firstName(name);
  const part = partOfDay(at);
  const line = part === "evening" ? "Good night" : part === "morning" ? "Have a good day" : "Have a good afternoon";
  return who ? `${line}, ${who}.` : `${line}.`;
}
